import type { Repeat, Weekday } from "../types";
import type { InteractiveOutcome } from "./interactive";

/**
 * The schedule card's reading of `propose_schedule` (src/profile/components.ts): the model's
 * arguments, an existing schedule it changes, and the answer. Plain .ts so it runs under `node --test`.
 */

export interface ProposalFields {
	title: string;
	repeat: Repeat;
	text: string;
	notify: boolean;
}

export type ReadProposal = { ok: true; id: string | undefined; fields: ProposalFields } | { ok: false; error: string };

const WEEKDAYS: readonly Weekday[] = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

function isPlainObject(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** `8:05` or `08:05` as `08:05`; undefined for anything that is not a time of day. */
function readTime(value: unknown): string | undefined {
	const match = typeof value === "string" ? /^(\d{1,2}):(\d{2})$/.exec(value.trim()) : null;
	if (!match) return undefined;
	const [hour, minute] = [Number(match[1]), Number(match[2])];
	if (hour > 23 || minute > 59) return undefined;
	return `${String(hour).padStart(2, "0")}:${match[2]}`;
}

/** A weekday or day the form cannot show starts on 周一 or 1 号, where the user sees it before saving. */
function readRepeat(value: unknown): Repeat | undefined {
	if (!isPlainObject(value)) return undefined;
	const at = readTime(value.at);
	if (!at) return undefined;
	switch (value.every) {
		case "day":
			return { every: "day", at };
		case "week":
			return { every: "week", at, weekday: WEEKDAYS.includes(value.weekday as Weekday) ? (value.weekday as Weekday) : "mon" };
		case "month": {
			if (value.day === "last") return { every: "month", at, day: "last" };
			const day = Number(value.day);
			return { every: "month", at, day: Number.isInteger(day) && day >= 1 && day <= 28 ? day : 1 };
		}
		default:
			return undefined;
	}
}

export function readProposal(args: Record<string, unknown>): ReadProposal {
	const repeat = readRepeat(args.repeat);
	if (!repeat) return { ok: false, error: "propose_schedule 的 repeat 无法解析：需要 every（day/week/month）和 HH:MM 格式的 at" };
	return {
		ok: true,
		id: typeof args.id === "string" && args.id ? args.id : undefined,
		fields: {
			title: typeof args.title === "string" ? args.title : "",
			repeat,
			text: typeof args.text === "string" ? args.text : "",
			notify: typeof args.notify === "boolean" ? args.notify : true,
		},
	};
}

function sameRepeat(a: Repeat | null, b: Repeat): boolean {
	return a !== null && JSON.stringify(readRepeat(a)) === JSON.stringify(readRepeat(b));
}

/** The fields a proposed change differs in from the schedule as it stands. */
export function scheduleDiff(current: Omit<ProposalFields, "repeat"> & { repeat: Repeat | null }, proposed: ProposalFields): Array<keyof ProposalFields> {
	const changed: Array<keyof ProposalFields> = [];
	if (current.title !== proposed.title) changed.push("title");
	if (!sameRepeat(current.repeat, proposed.repeat)) changed.push("repeat");
	if (current.text !== proposed.text) changed.push("text");
	if (current.notify !== proposed.notify) changed.push("notify");
	return changed;
}

export type ProposalAnswer =
	| { kind: "saved"; fields: ProposalFields; paused: boolean; nextRun: string | null }
	| { kind: "declined" }
	| { kind: "gone" }
	| { kind: "unreadable" };

/** What the call's result says; undefined while it is still waiting. */
export function readAnswer(outcome: InteractiveOutcome | undefined): ProposalAnswer | undefined {
	if (!outcome) return undefined;
	if (outcome.kind === "cancelled") return { kind: "gone" };
	if (outcome.kind === "unparseable") return { kind: "unreadable" };
	const value = outcome.value;
	if (value.saved === false) return { kind: "declined" };
	const repeat = readRepeat(value.repeat);
	if (value.saved !== true || !repeat || typeof value.title !== "string" || typeof value.text !== "string") return { kind: "unreadable" };
	return {
		kind: "saved",
		fields: { title: value.title, repeat, text: value.text, notify: value.notify === true },
		paused: value.paused === true,
		nextRun: typeof value.nextRun === "string" ? value.nextRun : null,
	};
}
